import type { ResizeHandle, ShapeTransform } from './shapes/interfaces/IShape';

/** Minimum size in world-space pixels for any dimension. */
const MIN_SIZE = 20;

/**
 * Pure function: given a shape's starting transform, the current mouse
 * world-space position, and the active resize handle, returns the new
 * transform with aspect-ratio locked by default.
 *
 * Extracted from ShapeManager so it can be unit-tested without any
 * PixiJS or DOM dependencies.
 */
export function calculateResizeTransform(
    s: ShapeTransform,
    mouseWorldX: number,
    mouseWorldY: number,
    handle: ResizeHandle
): ShapeTransform {
    const ar = s.width / s.height;

    let newX = s.x;
    let newY = s.y;
    let newWidth = s.width;
    let newHeight = s.height;

    switch (handle) {
        case 'bottom-right': {
            const scale = Math.max(
                (mouseWorldX - s.x) / s.width,
                (mouseWorldY - s.y) / s.height
            );
            newWidth  = Math.max(MIN_SIZE, s.width * scale);
            newHeight = newWidth / ar;
            break;
        }
        case 'bottom-left': {
            const scale = Math.max(
                ((s.x + s.width) - mouseWorldX) / s.width,
                (mouseWorldY - s.y) / s.height
            );
            newWidth  = Math.max(MIN_SIZE, s.width * scale);
            newHeight = newWidth / ar;
            newX = s.x + s.width - newWidth;
            break;
        }
        case 'top-right': {
            const scale = Math.max(
                (mouseWorldX - s.x) / s.width,
                ((s.y + s.height) - mouseWorldY) / s.height
            );
            newWidth  = Math.max(MIN_SIZE, s.width * scale);
            newHeight = newWidth / ar;
            newY = s.y + s.height - newHeight;
            break;
        }
        case 'top-left': {
            const scale = Math.max(
                ((s.x + s.width) - mouseWorldX) / s.width,
                ((s.y + s.height) - mouseWorldY) / s.height
            );
            newWidth  = Math.max(MIN_SIZE, s.width * scale);
            newHeight = newWidth / ar;
            newX = s.x + s.width - newWidth;
            newY = s.y + s.height - newHeight;
            break;
        }
        case 'right':
            newWidth  = Math.max(MIN_SIZE, mouseWorldX - s.x);
            newHeight = newWidth / ar;
            break;
        case 'left':
            newWidth  = Math.max(MIN_SIZE, (s.x + s.width) - mouseWorldX);
            newHeight = newWidth / ar;
            newX = s.x + s.width - newWidth;
            break;
        case 'bottom':
            newHeight = Math.max(MIN_SIZE, mouseWorldY - s.y);
            newWidth  = newHeight * ar;
            break;
        case 'top':
            newHeight = Math.max(MIN_SIZE, (s.y + s.height) - mouseWorldY);
            newWidth  = newHeight * ar;
            newY = s.y + s.height - newHeight;
            break;
    }

    // Post-clamp: ensure both dimensions ≥ MIN_SIZE while preserving AR.
    // If either dimension is below the minimum, scale both up uniformly and
    // re-anchor the origin for handles that move the top or left edge.
    const minScale = Math.max(MIN_SIZE / newWidth, MIN_SIZE / newHeight, 1);
    if (minScale > 1) {
        newWidth  *= minScale;
        newHeight *= minScale;
        if (handle === 'bottom-left' || handle === 'top-left' || handle === 'left') {
            newX = s.x + s.width - newWidth;
        }
        if (handle === 'top-right' || handle === 'top-left' || handle === 'top') {
            newY = s.y + s.height - newHeight;
        }
    }

    return { x: newX, y: newY, width: newWidth, height: newHeight };
}
